import { IconX } from '@tabler/icons-solidjs'
import {
  createEffect,
  createUniqueId,
  type JSX,
  onCleanup,
  onMount,
  Show,
  splitProps,
} from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'

const modalSheetStack: HTMLElement[] = []
const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

const sheet = tv({
  slots: {
    scrim: [
      'fixed inset-0 z-50 bg-ink/24',
      'transition-[background-color,opacity,visibility] duration-(--duration-overlay)',
      'ease-(--ease-overlay)',
    ],
    panel: [
      'absolute inset-x-2 bottom-2 flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden',
      'rounded-panel border border-border bg-surface text-text shadow-sheet',
      'transition-[opacity,translate,visibility] duration-(--duration-overlay)',
      'ease-(--ease-overlay) motion-reduce:translate-x-0 motion-reduce:translate-y-0',
      'md:inset-y-2 md:right-2 md:left-auto md:h-[calc(100dvh-1rem)] md:w-[min(32rem,calc(100vw-1rem))]',
    ],
    header:
      'flex shrink-0 items-start justify-between gap-4 border-b border-border bg-surface-muted px-4 py-3',
    identity: 'flex min-w-0 items-start gap-3',
    icon: 'grid size-8 shrink-0 place-items-center rounded-control bg-ink text-text-inverse',
    heading: 'grid min-w-0 gap-0.5',
    title: 'text-ui font-semibold tracking-tight text-text',
    description: 'text-body text-text-subtle',
    close: [
      'flex size-8 shrink-0 items-center justify-center rounded-control text-text-subtle max-md:size-(--size-touch)',
      'transition-colors duration-(--duration-press) ease-out',
      'hover:bg-surface-sunken hover:text-text',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
    ],
    body: 'min-h-0 flex-1 overflow-y-auto p-4',
    footer:
      'flex shrink-0 items-center justify-end gap-2 border-t border-border bg-surface-muted px-4 py-3',
  },
  variants: {
    /**
     * `modal` gently dims everything behind the panel at every width — a sheet is where the
     * decision is, and on a wide screen an undimmed board behind it reads as still editable.
     * `companion` keeps the page fully visible and interactive; the assistant is meant to sit
     * beside the work rather than in front of it.
     */
    scrimStyle: {
      modal: {},
      companion: { scrim: 'lg:bg-transparent lg:backdrop-blur-none' },
    },
    size: {
      default: {},
      record: {
        panel:
          'inset-0 max-h-dvh rounded-none border-0 shadow-none md:inset-y-2 md:right-2 md:h-[calc(100dvh-1rem)] md:w-[min(56rem,calc(100vw-3rem))] md:rounded-panel md:border md:border-border md:shadow-sheet',
        header: 'bg-surface px-5 py-4 md:px-6',
        title: 'text-title font-bold',
        body: 'p-0',
        footer: 'bg-surface px-5 py-3 md:px-6',
      },
    },
  },
  defaultVariants: { scrimStyle: 'modal', size: 'default' },
})

/**
 * Dialog — a decision that must be answered now, with a small payload: confirm, destroy,
 * quick-create. Sheet — editing a record with many fields, or work where the board should remain
 * visible. Sheets bottom-anchor on mobile and side-anchor right on larger screens.
 */
export interface SheetProps extends Omit<JSX.HTMLAttributes<HTMLElement>, 'children' | 'title'> {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: JSX.Element
  closeLabel?: string
  /** Prevents light-dismiss from the scrim while typed data is dirty. */
  dismissable?: boolean
  icon?: JSX.Element
  footer?: JSX.Element
  /** Persistent controls or record context rendered between identity and scrolling content. */
  headerContent?: JSX.Element
  bodyClass?: string
  initialFocus?: string
  restoreFocus?: boolean
  /** `companion` leaves the page undimmed on large screens. Defaults to `modal`. */
  scrimStyle?: 'modal' | 'companion'
  /** Wider workspace for a record with several editable sections. */
  size?: 'default' | 'record'
  children: JSX.Element
}

export const Sheet = (props: SheetProps) => {
  const [local, rest] = splitProps(props, [
    'open',
    'onOpenChange',
    'title',
    'description',
    'closeLabel',
    'dismissable',
    'icon',
    'footer',
    'headerContent',
    'bodyClass',
    'initialFocus',
    'restoreFocus',
    'scrimStyle',
    'size',
    'children',
    'class',
  ])
  const styles = () => sheet({ scrimStyle: local.scrimStyle, size: local.size })
  const titleId = createUniqueId()
  const descriptionId = createUniqueId()
  let scrim!: HTMLDivElement
  let panel!: HTMLElement
  let returnFocus: HTMLElement | undefined
  let wasOpen = false

  createEffect(() => {
    const isOpen = local.open
    if (isOpen && !wasOpen) {
      returnFocus = document.activeElement as HTMLElement | undefined
    } else if (!isOpen && wasOpen && local.restoreFocus !== false) {
      const frame = requestAnimationFrame(() => returnFocus?.focus())
      onCleanup(() => cancelAnimationFrame(frame))
    }
    wasOpen = isOpen
  })

  onMount(() => {
    const lightDismiss = (event: MouseEvent): void => {
      if (event.target === scrim && local.dismissable !== false) local.onOpenChange(false)
    }
    scrim.addEventListener('click', lightDismiss)

    createEffect(() => {
      if (!local.open) return
      const isModal = local.scrimStyle !== 'companion'
      if (isModal) modalSheetStack.push(panel)

      const onKeyDown = (event: KeyboardEvent): void => {
        if (isModal && modalSheetStack.at(-1) !== panel) return
        if (!(event.target instanceof Node)) return
        if (isModal && !panel.contains(event.target)) return
        if (!isModal && event.target instanceof Element && event.target.closest('dialog[open]'))
          return

        if (
          event.key === 'Escape' &&
          event.target instanceof Element &&
          event.target.closest('[role="menu"]')
        )
          return
        if (event.key === 'Escape' && local.dismissable !== false) {
          event.preventDefault()
          local.onOpenChange(false)
          return
        }
        if (event.key !== 'Tab' || !isModal) return

        const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
          (element) => element.getClientRects().length > 0,
        )
        const first = focusable[0] ?? panel
        const last = focusable.at(-1) ?? panel
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
      const onFocusIn = (event: FocusEvent): void => {
        if (!isModal || modalSheetStack.at(-1) !== panel) return
        if (!(event.target instanceof HTMLElement) || panel.contains(event.target)) return
        if (event.target.closest('dialog[open]')) return
        ;(panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel).focus()
      }
      document.addEventListener('keydown', onKeyDown)
      document.addEventListener('focusin', onFocusIn)
      onCleanup(() => {
        document.removeEventListener('keydown', onKeyDown)
        document.removeEventListener('focusin', onFocusIn)
        const stackIndex = modalSheetStack.lastIndexOf(panel)
        if (stackIndex >= 0) modalSheetStack.splice(stackIndex, 1)
      })
    })

    createEffect(() => {
      if (!local.open) return
      let focusFrame = 0
      const settleFrame = requestAnimationFrame(() => {
        focusFrame = requestAnimationFrame(() => {
          if (document.activeElement !== panel && panel.contains(document.activeElement)) return
          const focusable =
            (local.initialFocus
              ? panel.querySelector<HTMLElement>(local.initialFocus)
              : undefined) ??
            panel.querySelector<HTMLElement>('[autofocus]') ??
            panel.querySelector<HTMLElement>(FOCUSABLE)
          ;(focusable ?? panel).focus()
        })
      })
      onCleanup(() => {
        cancelAnimationFrame(settleFrame)
        cancelAnimationFrame(focusFrame)
      })
    })

    onCleanup(() => scrim.removeEventListener('click', lightDismiss))
  })

  return (
    <div
      ref={scrim}
      class={cn(
        styles().scrim(),
        local.open ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0',
      )}
      aria-hidden={!local.open}
    >
      <aside
        {...rest}
        ref={panel}
        class={cn(
          styles().panel(),
          local.open
            ? 'translate-y-0 opacity-100 md:translate-x-0'
            : 'translate-y-4 opacity-0 md:translate-x-4 md:translate-y-0',
          local.class,
        )}
        role="dialog"
        aria-modal={local.scrimStyle === 'companion' ? undefined : 'true'}
        aria-labelledby={titleId}
        aria-describedby={local.description ? descriptionId : undefined}
        data-open={local.open}
        inert={!local.open}
        tabIndex={-1}
      >
        <header class={styles().header()}>
          <div class={styles().identity()}>
            <Show when={local.icon}>{(icon) => <span class={styles().icon()}>{icon()}</span>}</Show>
            <div class={styles().heading()}>
              <h2 id={titleId} class={styles().title()}>
                {local.title}
              </h2>
              <Show when={local.description}>
                {(description) => (
                  <div id={descriptionId} class={styles().description()}>
                    {description()}
                  </div>
                )}
              </Show>
            </div>
          </div>
          <button
            type="button"
            class={styles().close()}
            aria-label={local.closeLabel ?? 'Закрити'}
            onClick={() => local.onOpenChange(false)}
          >
            <IconX size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </header>
        <Show when={local.headerContent}>
          {(content) => (
            <div class="shrink-0 border-b border-border bg-surface px-5 py-4 md:px-6">
              {content()}
            </div>
          )}
        </Show>
        <div class={cn(styles().body(), local.bodyClass)}>{local.children}</div>
        <Show when={local.footer}>
          {(footer) => <footer class={styles().footer()}>{footer()}</footer>}
        </Show>
      </aside>
    </div>
  )
}
